const TRYBOOKING_WIDGET_SCRIPT = 'https://www.trybooking.com/widget.js';
const TRYBOOKING_ORIGIN = 'https://www.trybooking.com';
const DEFAULT_REGION = 'au';

let scriptPromise = null;

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function parseHeight(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = parseInt(value.replace(/px$/i, ''), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function getViewportIframeHeight() {
  if (typeof window === 'undefined') return 720;
  // Keep a practical minimum without forcing nearly full-viewport height,
  // which causes the iframe to swallow the first page scroll attempt.
  return Math.min(Math.max(window.innerHeight - 220, 520), 860);
}

function applyWidgetHeight(iframe, height) {
  if (!iframe) return;

  const parsed = parseHeight(height);
  const nextHeight = Math.max(parsed || 0, getViewportIframeHeight());
  const px = `${nextHeight}px`;

  iframe.style.height = px;
  iframe.style.minHeight = px;

  const container = iframe.parentElement;
  if (container) {
    container.style.minHeight = px;
  }
}

function extractHeightMessage(data) {
  if (!data || typeof data !== 'object') return 0;

  return (
    parseHeight(data.height) ||
    parseHeight(data?.message?.height) ||
    parseHeight(data?.payload?.height) ||
    0
  );
}

function ensureTryBookingMessageListener() {
  if (typeof window === 'undefined' || window.__trybMessageListenerAttached) return;

  window.__trybMessageListenerAttached = true;
  window.__trybWidgetIframes = window.__trybWidgetIframes || new Set();

  window.addEventListener(
    'message',
    (event) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      for (const iframe of window.__trybWidgetIframes) {
        if (iframe.contentWindow === event.source) {
          const height = extractHeightMessage(data);
          if (height) {
            applyWidgetHeight(iframe, height);
          }
          break;
        }
      }
    },
    false,
  );
}

function trackTryBookingIframe(iframe) {
  ensureTryBookingMessageListener();
  if (iframe) window.__trybWidgetIframes.add(iframe);
}

export function loadTryBookingWidgetScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('TryBooking widget requires a browser environment'));
  }

  if (window.__trybWidgetScriptLoaded) {
    return Promise.resolve();
  }

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${TRYBOOKING_WIDGET_SCRIPT}"]`);

      if (existing) {
        const done = () => {
          window.__trybWidgetScriptLoaded = true;
          resolve();
        };

        if (existing.dataset.loaded === 'true') {
          done();
          return;
        }

        existing.addEventListener('load', () => {
          existing.dataset.loaded = 'true';
          done();
        });
        existing.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      script.src = TRYBOOKING_WIDGET_SCRIPT;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        script.dataset.loaded = 'true';
        window.__trybWidgetScriptLoaded = true;
        resolve();
      };
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  return scriptPromise;
}

function buildWidgetIframeUrl(container, eventId, widgetType = 'fullEmbed', region = DEFAULT_REGION) {
  const uid = `${Date.now()}-${eventId}`;
  const params = new URLSearchParams(window.location.search);

  Object.entries(container.dataset).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });

  params.set('origin', window.location.origin);
  params.set('uid', uid);
  params.append('embed', '');
  params.set('widget', 'true');

  const base = `${TRYBOOKING_ORIGIN}/${region}/apiv2/Events/${eventId}/widget/${widgetType}`;
  return { url: `${base}?${params.toString()}`, uid };
}

function bindIframeResize(iframe) {
  if (!iframe || typeof window.iFrameResize !== 'function') return;

  const initialHeight = getViewportIframeHeight();

  window.iFrameResize(
    {
      heightCalculationMethod: 'taggedElement',
      warningTimeout: 0,
      checkOrigin: false,
      scrolling: true,
      minHeight: initialHeight,
      onResized: ({ height }) => applyWidgetHeight(iframe, height),
    },
    iframe,
  );
}

export function mountTryBookingWidget(container, eventId, widgetType = 'fullEmbed', region = DEFAULT_REGION) {
  if (!container || !eventId) return null;

  const existing = container.querySelector('iframe');
  if (existing) return existing;

  const { url, uid } = buildWidgetIframeUrl(container, eventId, widgetType, region);
  const initialHeight = getViewportIframeHeight();

  const iframe = document.createElement('iframe');
  iframe.id = uid;
  iframe.src = url;
  iframe.title = 'TryBooking checkout';
  iframe.allow = 'payment';
  iframe.setAttribute('scrolling', 'yes');
  iframe.style.width = '100%';
  iframe.style.border = '0';
  iframe.style.display = 'block';
  iframe.style.height = `${initialHeight}px`;
  iframe.style.minHeight = `${initialHeight}px`;
  iframe.style.overflow = 'auto';
  iframe.style.webkitOverflowScrolling = 'touch';

  container.innerHTML = '';
  container.appendChild(iframe);
  container.style.minHeight = `${initialHeight}px`;
  container.style.overflow = 'visible';

  trackTryBookingIframe(iframe);
  bindIframeResize(iframe);

  return iframe;
}

export async function ensureTryBookingWidget(container, eventId, widgetType = 'fullEmbed', region = DEFAULT_REGION) {
  await loadTryBookingWidgetScript();

  if (!container) return null;

  let iframe = container.querySelector('iframe');
  if (!iframe) {
    iframe = mountTryBookingWidget(container, eventId, widgetType, region);
  } else {
    trackTryBookingIframe(iframe);
    bindIframeResize(iframe);
  }

  if (!iframe) return null;

  await new Promise((resolve) => {
    if (iframe.dataset.loaded === 'true') {
      resolve();
      return;
    }

    const finish = () => {
      iframe.dataset.loaded = 'true';
      resolve();
    };

    iframe.addEventListener('load', finish, { once: true });
    iframe.addEventListener('error', finish, { once: true });
    setTimeout(finish, 15000);
  });

  return iframe;
}

export function buildTryBookingFallbackUrl(eventId, region = DEFAULT_REGION) {
  return `${TRYBOOKING_ORIGIN}/${region}/event/${eventId}/tickets`;
}
